import {
  createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState,
} from 'react';

/**
 * Tooltips that work with a mouse and with a thumb.
 *
 * Desktop hovers. Touch devices have no hover at all, and the elements that
 * most need a tooltip here -- move buttons -- already do something on tap, so
 * touch uses a long press and then swallows the click that would otherwise
 * fire the move.
 */

type Render = () => React.ReactNode;

interface TooltipApi {
  show(anchor: HTMLElement, render: Render, sticky: boolean): void;
  hide(): void;
}

const Ctx = createContext<TooltipApi | null>(null);

interface Active {
  /** Kept as the element, not a measured rect: the battle log scrolls itself
   *  constantly, and the tooltip should follow its anchor rather than vanish. */
  anchor: HTMLElement;
  render: Render;
  /** Opened by touch: needs an explicit dismiss rather than pointer-leave. */
  sticky: boolean;
}

const LONG_PRESS_MS = 350;
const MARGIN = 8;

export function TooltipProvider({ children }: { children: React.ReactNode }) {
  const [active, setActive] = useState<Active | null>(null);

  const api = useMemo<TooltipApi>(
    () => ({
      show: (anchor, render, sticky) => setActive({ anchor, render, sticky }),
      hide: () => setActive(null),
    }),
    []
  );

  // Scrolling moves the anchor, so re-measure rather than close. Closing here
  // would dismiss the tooltip every time the battle log auto-scrolls, which is
  // most turns.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!active) return;
    const reposition = () => {
      if (!active.anchor.isConnected) setActive(null);
      else setTick((t) => t + 1);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setActive(null);
    };
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
      window.removeEventListener('keydown', onKey);
    };
  }, [active]);

  return (
    <Ctx.Provider value={api}>
      {children}
      {active && <Panel key={0} active={active} tick={tick} onDismiss={() => setActive(null)} />}
    </Ctx.Provider>
  );
}

function Panel({ active, tick, onDismiss }: { active: Active; tick: number; onDismiss: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const box = el.getBoundingClientRect();
    const anchor = active.anchor.getBoundingClientRect();

    // Prefer above the anchor; fall back below when there is no room.
    let top = anchor.top - box.height - MARGIN;
    if (top < MARGIN) {
      const below = anchor.bottom + MARGIN;
      top = below + box.height + MARGIN > window.innerHeight
        ? Math.max(MARGIN, window.innerHeight - box.height - MARGIN)
        : below;
    }

    // Centre on the anchor, then pull back inside the viewport.
    let left = anchor.left + anchor.width / 2 - box.width / 2;
    left = Math.min(Math.max(MARGIN, left), window.innerWidth - box.width - MARGIN);

    setPos({ left, top });
  }, [active, tick]);

  return (
    <>
      {active.sticky && <div className="tooltip-backdrop" onPointerDown={onDismiss} />}
      <div
        ref={ref}
        className="tooltip"
        role="tooltip"
        style={{
          left: pos?.left ?? 0,
          top: pos?.top ?? 0,
          // Avoid a flash at 0,0 before the first measurement.
          visibility: pos ? 'visible' : 'hidden',
        }}
      >
        {active.render()}
      </div>
    </>
  );
}

export interface TriggerProps {
  onPointerEnter: (e: React.PointerEvent) => void;
  onPointerLeave: (e: React.PointerEvent) => void;
  onPointerDown: (e: React.PointerEvent) => void;
  onPointerUp: (e: React.PointerEvent) => void;
  onPointerCancel: (e: React.PointerEvent) => void;
  onContextMenu: (e: React.MouseEvent) => void;
  onClickCapture: (e: React.MouseEvent) => void;
}

/**
 * Spread the returned props onto any element to give it a tooltip.
 * `render` is only called when the tooltip actually opens.
 */
export function useTooltip(render: Render | null): TriggerProps {
  const api = useContext(Ctx);
  const timer = useRef<number | undefined>(undefined);
  /** Set when a long press opened the tooltip, so the tap does not also act. */
  const swallowClick = useRef(false);

  const clearTimer = useCallback(() => {
    if (timer.current !== undefined) {
      window.clearTimeout(timer.current);
      timer.current = undefined;
    }
  }, []);

  useEffect(() => clearTimer, [clearTimer]);

  return {
    onPointerEnter: (e) => {
      if (!api || !render || e.pointerType !== 'mouse') return;
      api.show(e.currentTarget as HTMLElement, render, false);
    },
    onPointerLeave: (e) => {
      clearTimer();
      if (!api || e.pointerType !== 'mouse') return;
      api.hide();
    },
    onPointerDown: (e) => {
      if (!api || !render || e.pointerType === 'mouse') return;
      const el = e.currentTarget as HTMLElement;
      clearTimer();
      timer.current = window.setTimeout(() => {
        swallowClick.current = true;
        api.show(el, render, true);
      }, LONG_PRESS_MS);
    },
    onPointerUp: clearTimer,
    onPointerCancel: clearTimer,
    // A long press on mobile otherwise raises the native context menu.
    onContextMenu: (e) => {
      if (swallowClick.current) e.preventDefault();
    },
    onClickCapture: (e) => {
      if (!swallowClick.current) return;
      swallowClick.current = false;
      // Capture phase runs before this element's own onClick, so stopping here
      // prevents the long press from also selecting the move.
      e.preventDefault();
      e.stopPropagation();
    },
  };
}
